import React, { useState, useRef, useEffect } from 'react'
import { LANG_LABELS } from '../libs/templateI18n'

// Real regional flags rendered as inline SVG. Native <select><option> can only
// show text/emoji, and the Spanish co-official languages (ca/gl/eu) have no
// reliable flag emoji — they render as a generic black flag on almost every
// platform. So we draw them ourselves and use a custom dropdown instead.
const Flag = ({ code }) => {
  const box = {
    width: 20,
    height: 14,
    display: 'inline-block',
    borderRadius: 2
  }
  const common = {
    style: box,
    preserveAspectRatio: 'none',
    className: 'ring-1 ring-black/10 shrink-0'
  }
  switch (code) {
    case 'es': // España
      return (
        <svg viewBox="0 0 3 2" {...common}>
          <rect width="3" height="2" fill="#AA151B" />
          <rect y="0.5" width="3" height="1" fill="#F1BF00" />
        </svg>
      )
    case 'ca': // Catalunya — Senyera
      return (
        <svg viewBox="0 0 3 9" {...common}>
          <rect width="3" height="9" fill="#FCDD09" />
          <rect y="1" width="3" height="1" fill="#DA121A" />
          <rect y="3" width="3" height="1" fill="#DA121A" />
          <rect y="5" width="3" height="1" fill="#DA121A" />
          <rect y="7" width="3" height="1" fill="#DA121A" />
        </svg>
      )
    case 'gl': // Galicia
      return (
        <svg viewBox="0 0 30 20" {...common}>
          <rect width="30" height="20" fill="#FFFFFF" />
          <line x1="0" y1="0" x2="30" y2="20" stroke="#1560BD" strokeWidth="4.4" />
        </svg>
      )
    case 'eu': // Euskadi — Ikurriña
      return (
        <svg viewBox="0 0 30 20" {...common}>
          <rect width="30" height="20" fill="#D52B1E" />
          <path d="M0,0 L30,20 M30,0 L0,20" stroke="#009B48" strokeWidth="3.6" />
          <path d="M15,0 V20 M0,10 H30" stroke="#FFFFFF" strokeWidth="3.6" />
        </svg>
      )
    case 'fr': // France
      return (
        <svg viewBox="0 0 3 2" {...common}>
          <rect width="1" height="2" fill="#0055A4" />
          <rect x="1" width="1" height="2" fill="#FFFFFF" />
          <rect x="2" width="1" height="2" fill="#EF4135" />
        </svg>
      )
    case 'en': // English — Union Jack
      return (
        <svg viewBox="0 0 60 30" {...common}>
          <rect width="60" height="30" fill="#012169" />
          <path d="M0,0 L60,30 M60,0 L0,30" stroke="#FFFFFF" strokeWidth="6" />
          <path d="M0,0 L60,30 M60,0 L0,30" stroke="#C8102E" strokeWidth="2.4" />
          <path d="M30,0 V30 M0,15 H60" stroke="#FFFFFF" strokeWidth="10" />
          <path d="M30,0 V30 M0,15 H60" stroke="#C8102E" strokeWidth="6" />
        </svg>
      )
    default:
      return (
        <span
          style={box}
          className="ring-1 ring-black/10 bg-gray-200 rounded-[2px]"
        />
      )
  }
}

// Compact dropdown to switch the language used to render a template preview.
// Hidden entirely when the template only has the base language (nothing to
// switch to). When shown, "Castellano" is always available as the base.
const LanguageSelector = ({ langs, value, onChange, className = '' }) => {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  if (!Array.isArray(langs) || langs.length <= 1) return null

  const pick = (code) => {
    if (onChange) onChange(code)
    setOpen(false)
  }

  return (
    <div ref={ref} className={`relative inline-flex items-center gap-1.5 ${className}`}>
      <span className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold">
        Idioma
      </span>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 text-xs bg-white border border-gray-300 rounded-md px-2 py-1 hover:border-indigo-400 focus:outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400"
      >
        <Flag code={value} />
        <span>{LANG_LABELS[value] || value}</span>
        <svg width="10" height="10" viewBox="0 0 20 20" className="text-gray-400">
          <path
            d="M5 8l5 5 5-5"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {open && (
        <ul
          role="listbox"
          className="absolute top-full right-0 mt-1 z-50 min-w-[132px] bg-white border border-gray-200 rounded-md shadow-lg py-1 max-h-64 overflow-auto"
        >
          {langs.map((code) => (
            <li key={code} role="option" aria-selected={code === value}>
              <button
                type="button"
                onClick={() => pick(code)}
                className={`w-full flex items-center gap-2 px-2.5 py-1.5 text-xs text-left hover:bg-indigo-50 ${
                  code === value ? 'bg-indigo-50/60 font-semibold' : ''
                }`}
              >
                <Flag code={code} />
                <span>{LANG_LABELS[code] || code}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export default LanguageSelector
